import Router from "next/router";
import { normalizeDashboardPathname } from "@/hooks/useDashboardRouter";

const SESSIONS_ROUTE = "/[org]/sessions/[[...sessionPath]]";
const BOT_USER_AGENT =
  /HeadlessChrome|Headless|bot\b|crawl|spider|slurp|Lighthouse|PhantomJS|Puppeteer|Playwright/i;

export function isBotWebVitalsClient(): boolean {
  return (
    typeof navigator !== "undefined" &&
    (navigator.webdriver === true || BOT_USER_AGENT.test(navigator.userAgent))
  );
}

/**
 * The new-session page (`/{org}/sessions/new`) shares the sessions catch-all
 * route pattern with session threads; report it under its own label so the
 * two chart separately in Grafana. The session segment comes from `asPath`
 * (`/acme/sessions/new` → `new`) rather than `Router.query`, which is still
 * empty when TTFB and LCP fire on a statically optimized first load.
 *
 * The `next/router` singleton has no instance under the App Router (its
 * getters throw), so the route then comes from `window.location`.
 */
export function reportedRoute(): string | null {
  const router = Router.router;
  const pathname =
    router?.pathname ??
    (typeof window !== "undefined"
      ? normalizeDashboardPathname(window.location.pathname)
      : null);
  if (!pathname) return null;
  if (pathname !== SESSIONS_ROUTE) return pathname;
  const path = (router?.asPath ?? window.location.pathname).split(/[?#]/)[0];
  const sessionSegment = path.split("/").filter(Boolean)[2];
  return sessionSegment === "new" ? "/[org]/sessions/new" : SESSIONS_ROUTE;
}

function buildShaField(): Record<string, string> {
  const buildSha = process.env.NEXT_PUBLIC_BUILD_SHA;
  return buildSha && /^[0-9a-f]{7,40}$/i.test(buildSha)
    ? { build_sha: buildSha }
    : {};
}

/**
 * Sent via `fetch` with `keepalive` and a form-encoded body: the
 * CORS-safelisted content type needs no preflight, which browsers often
 * cancel during unload, exactly when INP and CLS fire.
 */
export function postFrontendMetric(fields: Record<string, string>): void {
  void fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/frontend/metrics`, {
    method: "POST",
    keepalive: true,
    credentials: "include",
    body: new URLSearchParams({ ...buildShaField(), ...fields }),
  }).catch(() => {});
}

/**
 * Kept apart from `useWebVitalsReporting` so components can report a vital
 * without importing `next/web-vitals`, whose bundle reads `__dirname` and
 * breaks under Vite (Playwright component tests).
 */
export function reportWebVital(
  name: string,
  value: number,
  route: string,
): void {
  if (isBotWebVitalsClient()) return;

  postFrontendMetric({
    web_vital: name,
    web_vital_value: String(value),
    web_vital_route: route,
  });
}
